import React, { useState, useEffect } from 'react';
import { Lock, Menu, X, Home, Cpu, Shield, User, ArrowRight, CheckCircle, Rocket, Brain, BarChart3, Zap, Wifi, Layers, Mail } from 'lucide-react';
import './index.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Services Banner */}
      <div className="bg-blue-600 px-4 py-3">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white text-sm font-medium">
            🔒 Cybersecurity services — Phase 0
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-12 w-full bg-slate-900 border-b border-slate-800 z-50 transition-all duration-300 ${isScrolled ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Lock className="w-8 h-8 text-electric-blue" />
              <span className="text-xl font-bold">FSS</span>
            </div>
            
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="hover:text-electric-blue transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="hover:text-electric-blue transition-colors">About</button>
              <button onClick={() => scrollToSection('services')} className="hover:text-electric-blue transition-colors">Services</button>
              <button onClick={() => scrollToSection('platform')} className="hover:text-electric-blue transition-colors">Platform</button>
              <button onClick={() => scrollToSection('founder')} className="hover:text-electric-blue transition-colors">Founder</button>
              <button onClick={() => scrollToSection('contact')} className="hover:text-electric-blue transition-colors">Contact</button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800">
            <div className="px-4 py-2 space-y-2">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 hover:text-electric-blue">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 hover:text-electric-blue">About</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 hover:text-electric-blue">Services</button>
              <button onClick={() => scrollToSection('platform')} className="block w-full text-left py-2 hover:text-electric-blue">Platform</button>
              <button onClick={() => scrollToSection('founder')} className="block w-full text-left py-2 hover:text-electric-blue">Founder</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 hover:text-electric-blue">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-24 min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-navy to-slate-950"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-slate-900 rounded-full mb-8">
            <Lock className="w-16 h-16 text-electric-blue" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
            Smart Home & Cybersecurity
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto">
            We deliver practical security and automation solutions for homes and small businesses — from network hardening to smart home integration.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Request a Consultation</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scrollToSection('platform')}
              className="px-8 py-4 border border-blue-600 text-blue-400 rounded hover:bg-blue-600 hover:text-white transition-colors"
            >
              Explore Platform
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">About FSS</h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-lg text-slate-300 leading-relaxed">
                Founded by a cybersecurity and systems specialist, FSS Fortified Security Solutions delivers practical security and smart home solutions for homes and small businesses. We specialize in network security, intelligent automation, and privacy-first systems that keep your data local and your home protected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h2>
          </div>

          <div className="space-y-16">
            {/* Core Service - Smart Home Automation */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center space-x-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded mb-6">
                <Home className="w-4 h-4" />
                <span className="text-sm font-semibold">Core Focus</span>
              </div>
              <div className="inline-flex items-center space-x-2 bg-emerald/20 text-emerald px-3 py-1 rounded mb-4 ml-2">
                <Rocket className="w-3 h-3" />
                <span className="text-xs font-semibold">In Progress</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Smart Home Automation</h2>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
                Intelligent, privacy-first automation built on Home Assistant.
                Designed for local control, reliability, and total customization.
              </p>
            </div>

            {/* Main Automation Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {[
                {
                  icon: Zap,
                  title: "Custom Home Assistant",
                  items: ["Full configuration", "Custom dashboards", "Integration setup"]
                },
                {
                  icon: Brain,
                  title: "Full-Home Automation",
                  items: ["Lighting automation", "Climate control", "Presence detection"]
                },
                {
                  icon: Shield,
                  title: "Security Integration",
                  items: ["Smart locks", "Motion alerts", "Alarm scenes"]
                },
                {
                  icon: BarChart3,
                  title: "Energy Management",
                  items: ["Usage monitoring", "Cost optimization", "Peak shaving"]
                },
                {
                  icon: Wifi,
                  title: "Voice Control",
                  items: ["Local assistants", "Custom commands", "Privacy-first"]
                },
                {
                  icon: Layers,
                  title: "Device Integration",
                  items: ["Zigbee/Z-Wave", "Matter devices", "Legacy systems"]
                }
              ].map((service, index) => (
                <div
                  key={index}
                  className="bg-slate-800 p-6 border border-slate-700"
                >
                  <service.icon className="w-12 h-12 text-electric-blue mb-4" />
                  <h3 className="text-xl font-semibold mb-4">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center space-x-2 text-slate-300">
                        <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Automation Specializations */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div
                className="bg-slate-800 p-6 border border-slate-700"
              >
                <Brain className="w-10 h-10 text-emerald mb-4" />
                <h4 className="text-xl font-semibold mb-4 text-emerald">Intelligent Automations</h4>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Presence detection</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Adaptive lighting</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Energy-saving routines</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Climate control logic</span>
                  </li>
                </ul>
              </div>

              <div
                className="bg-slate-800 p-6 border border-slate-700"
              >
                <BarChart3 className="w-10 h-10 text-electric-blue mb-4" />
                <h4 className="text-xl font-semibold mb-4 text-electric-blue">Dashboards & Monitoring</h4>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Custom Lovelace dashboards</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Mobile control interfaces</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Real-time alerts</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Smart lock workflows</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Alarm scene control</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0" />
                    <span>Security automation</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Supporting Services */}
            <div
              className="text-center mb-12"
            >
              <h3 className="text-2xl font-bold mb-8">Supporting Services</h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Shield,
                  title: "Integrated Security Systems",
                  subtitle: "Security that works with your automation",
                  items: ["Camera installation", "Local NVR setups", "Smart alarm integration", "Motion-triggered automation"]
                },
                {
                  icon: Cpu,
                  title: "Network Infrastructure",
                  subtitle: "The backbone that powers your smart systems",
                  items: ["Secure home networking", "VLAN segmentation for IoT", "Firewall setup", "Business infrastructure"]
                },
                {
                  icon: Lock,
                  title: "System Hardening",
                  subtitle: "Optimization and security for your setup",
                  items: ["Vulnerability checks", "Network hardening", "Performance tuning", "Security audits"]
                }
              ].map((service, index) => (
                <div
                  key={index}
                  className="bg-slate-800 p-6 border border-slate-700"
                >
                  <service.icon className="w-10 h-10 text-slate-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
                  <p className="text-sm text-slate-400 mb-4">{service.subtitle}</p>
                  <ul className="space-y-2">
                    {service.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center space-x-2 text-slate-300">
                        <CheckCircle className="w-3 h-3 text-emerald flex-shrink-0" />
                        <span className="text-xs">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Section */}
      <section id="platform" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-emerald/20 text-emerald px-4 py-2 rounded mb-6">
              <Rocket className="w-4 h-4" />
              <span className="text-sm font-semibold">Startup – In Development</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">FSS Platform</h2>
            
            <div className="max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center space-x-2 bg-blue-500/20 text-blue-400 px-3 py-1 rounded mb-4">
                <Home className="w-3 h-3" />
                <span className="text-xs font-semibold">Powered by Home Assistant</span>
              </div>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Building a security-focused smart home platform with Home Assistant. Local control, intelligent automation, and network protection in one system.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 text-left">
                {[
                  "Local-first architecture",
                  "Advanced automation engine", 
                  "Custom dashboard builder",
                  "Multi-protocol support",
                  "Machine Learning integrations",
                  "Privacy by design",
                  "🤖 Predictive automation patterns",
                  "Neural network models"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-emerald flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="founder" className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center max-w-3xl mx-auto"
          >
            <User className="w-16 h-16 text-electric-blue mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Founder · Security & Systems Services</h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              Edwin Guerrero, founder of FSS Fortified Security Solutions, delivering practical security and IT solutions with a strong focus on privacy, performance, and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Simple Connect Section */}
      <section id="contact" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Let's Connect</h2>
            
            <div
              className="bg-slate-800 p-8 mb-8 border border-slate-700"
            >
              <p className="text-lg text-slate-300 mb-6">
                Ready to transform your home with intelligent automation? 
                We're here to help you build the perfect smart home ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="mailto:fsguard2@gmail.com" 
                  className="px-8 py-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Mail className="w-5 h-5" />
                  <span>Email Us</span>
                </a>
              </div>
            </div>

            <div
              className="grid md:grid-cols-3 gap-6"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600/20 rounded flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-semibold mb-2">Quick Response</h3>
                <p className="text-sm text-slate-400">Get replies within 24 hours</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald/20 rounded flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald" />
                </div>
                <h3 className="font-semibold mb-2">Free Consultation</h3>
                <p className="text-sm text-slate-400">No obligation initial assessment</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-semibold mb-2">Expert Support</h3>
                <p className="text-sm text-slate-400">Professional guidance every step</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Lock className="w-6 h-6 text-electric-blue" />
              <span className="font-semibold">FSS © 2026</span>
            </div>
            <p className="text-slate-400">Fortified. Secure. Local.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
